import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { resetALL } from 'Redux/actions';
import salesforceService from '../services/salesforceService';
import { CONVERSATION_STATUS, CONNECTION_STATUS } from '../services/constants';
import { CLEAR_REDUX } from 'Core/common/redux/actionTypes';
import { getConversationId, clearLastEventId, clearConversationStartTime } from '../services/webStorageService';
import { closeEventSource } from '../services/eventSourceService';

const initialState = {
  items: null,
  chat: null,
  loading: false,
  openModal: false,
  error: null,
  conversationsLoaded: false,  // Track if conversations have been fetched
  conversationsCount: 0,  // Track number of conversations found from API
  // Salesforce-specific state
  salesforce: {
    isInitialized: false,
    conversationId: null,
    previousConversationId: null, // Store old conversation ID for creating new session
    conversationStatus: CONVERSATION_STATUS.NOT_STARTED,
    connectionStatus: CONNECTION_STATUS.DISCONNECTED,
    isEventSourceConnected: false,
    isServiceReady: false,
    lastEventTimestamp: null,
    participantInfo: null,
    routingAttributes: {},
    agentName: null,
    // User context for authenticated sessions (userId, email, name from Cognito)
    userContext: null,
    // Session initialization state: 'not-started' | 'initializing' | 'initialized'
    // Persists across component unmount/remount (in Redux), cleared only on page reload
    sessionInitState: 'not-started',
    /** Track the online/offline status of the internet */
    isNetworkOnline: true,
    // Track message IDs sent from current device for cross-device sync
    sentMessageIds: [],
    // Agent availability tracking
    agentWaitTimer: {
      isWaiting: false,
      timeoutId: null,
      startTime: null,
      remainingTime: 30,  // 30 seconds default
      hasTimedOut: false,
      secondTimeout: false  // Track if second 30s timeout has occurred
    },
    // Track if agent has joined the conversation (for showing "agent joined" message)
    hasAgentJoined: false,
    // Timestamp when agent joined - used to position "agent joined" message between messages
    agentJoinedAt: null
  }
};

// Salesforce Async Thunks
export const initializeSalesforce = createAsyncThunk(
  'supportMessageListRedux/initializeSalesforce',
  async (config = null, { rejectWithValue }) => {
    try {
      const result = await salesforceService.initialize(config);
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const startSalesforceConversation = createAsyncThunk(
  'supportMessageListRedux/startSalesforceConversation',
  async ({ userContext, routingAttributes = {} }, { rejectWithValue, dispatch, getState }) => {
    try {
      // Set up event listeners before starting conversation
      salesforceService.addEventListener('CONVERSATION_MESSAGE', (event) => {
        try {
          const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

          // Parse the entryPayload like the sample app does
          let parsedMessage = eventData;
          if (eventData.conversationEntry && eventData.conversationEntry.entryPayload) {
            const entryPayload = JSON.parse(eventData.conversationEntry.entryPayload);
            parsedMessage = {
              ...eventData,
              messageText: entryPayload.abstractMessage?.staticContent?.text || '',
              text: entryPayload.abstractMessage?.staticContent?.text || '',
              senderRole: eventData.conversationEntry?.sender?.role,
              actorType: eventData.conversationEntry?.sender?.role,
              messageId: eventData.conversationEntry?.identifier,
              timestamp: eventData.conversationEntry?.transcriptedTimestamp,
              conversationId: eventData.conversationId,
              entryType: entryPayload.entryType
            };
          }

          dispatch(handleSalesforceMessage(parsedMessage));
        } catch (error) {
          console.error('[Salesforce:Redux] Failed to parse CONVERSATION_MESSAGE:', error, event.data);
        }
      });

      salesforceService.addEventListener('CONVERSATION_PARTICIPANT_CHANGED', (event) => {
        try {
          const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

          // ✅ FIX: Verify event is for CURRENT conversation (prevent old event replay)
          const currentConversationId = getConversationId();
          const eventConversationId = eventData?.conversationId;

          if (eventConversationId && currentConversationId && eventConversationId !== currentConversationId) {
            return; // Ignore events from old conversations
          }

          // Check participant changes for agent joining or leaving
          if (eventData?.conversationEntry?.entryPayload) {
            const entryPayload = JSON.parse(eventData.conversationEntry.entryPayload);

            if (entryPayload?.entries) {
              // Check for agent additions (agent accepting conversation)
              const agentAdditions = entryPayload.entries.filter(entry =>
                entry.operation === 'add' &&
                entry.participant?.role === 'Agent'
              );

              // Check for agent removals (conversation end)
              const agentRemovals = entryPayload.entries.filter(entry =>
                entry.operation === 'remove' &&
                entry.participant?.role === 'Agent'
              );

              if (agentAdditions.length > 0) {
                // ✅ NEW: Agent has joined the conversation!
                // Store agent name for later use in footer
                const agentName = agentAdditions[0]?.displayName || null;
                if (agentName) {
                  dispatch(setAgentName(agentName));
                }
                // Extract the actual timestamp from the event
                let agentJoinedTimestamp = extractEventDataTimestamp(eventData);
                // Stop the wait timer and activate conversation
                dispatch(agentJoinedConversation(agentJoinedTimestamp));
              } else if (agentRemovals.length > 0) {
                // Agent left - mark conversation as ended but keep data
                dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.ENDED));
                // Also update the service's internal status
                salesforceService.setConversationStatus(CONVERSATION_STATUS.ENDED);
                
                // NOTE: Do NOT delete conversation or clear conversation ID
                // Keep conversation data for history/reference
                // User can manually clear it if needed via the clearChat action
                
                // Clear any active typing indicators
                dispatch(setSalesforceTypingStatus({ isTyping: false }));
              }
            }
          }

          dispatch(updateSalesforceParticipant(eventData));
        } catch (error) {
          console.error('[Salesforce:Redux] Failed to parse CONVERSATION_PARTICIPANT_CHANGED:', error, event.data);
        }
      });

      salesforceService.addEventListener('CONVERSATION_ROUTING_WORK_RESULT', (event) => {
        try {
          console.log('[Salesforce:Redux] Raw CONVERSATION_ROUTING_WORK_RESULT event:', event);
          const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          console.log('[Salesforce:Redux] Parsed event data:', eventData);

          // ✅ FIX: Verify event is for CURRENT conversation (prevent old event replay)
          const currentConversationId = getConversationId();
          const eventConversationId = eventData?.conversationId;

          if (eventConversationId && currentConversationId && eventConversationId !== currentConversationId) {
            console.log('[Salesforce:Redux] Ignoring event from different conversation:', eventConversationId, 'vs', currentConversationId);
            return; // Ignore events from old conversations
          }
          
          // ✅ NEW: Start agent wait timer when conversation is routed
          // This means agents are available but none have accepted yet
          if (eventData?.conversationEntry?.entryPayload) {
            const entryPayload = JSON.parse(eventData.conversationEntry.entryPayload);
            console.log('[Salesforce:Redux] Entry payload:', entryPayload);
            
            // Check if this is an assignment event (workType: 'Assigned')
            if (entryPayload?.workType === 'Assigned') {
              console.log('[Salesforce:Redux] 🚀 STARTING AGENT WAIT TIMER - workType is Assigned');
              
              // ✅ FIX: Only start timer if not already waiting to prevent multiple timers
              const currentState = getState();
              const { agentWaitTimer } = currentState.supportMessageListRedux.salesforce;
              
              if (!agentWaitTimer.isWaiting && !agentWaitTimer.hasTimedOut) {
                console.log('[Salesforce:Redux] Starting new timer - no existing timer running');
                // Start the 30-second timer for agent to accept
                dispatch(startAgentWaitTimerAsync({ timeout: 30 }));
              } else {
                console.log('[Salesforce:Redux] Timer already running or timed out - ignoring duplicate assignment');
              }
            } else if (entryPayload?.workType === 'Declined') {
              console.log('[Salesforce:Redux] Agent declined conversation - ignoring for now');
              // ✅ SIMPLIFIED: Ignore all decline events for now
              // The modal reopen logic will handle restarting timers when needed
              console.log('[Salesforce:Redux] Decline events are ignored - modal reopen logic handles timer management');
            } else if (entryPayload?.workType === 'Accepted') {
              console.log('[Salesforce:Redux] Agent accepted conversation - stopping timer and setting ACTIVE');
              
              // ✅ Agent accepted - stop any running timer and set status to ACTIVE
              // ✅ Agent accepted - no need to set agentJoinedAt (only set at agent joined event)
              dispatch(agentJoinedConversation(null));
              
            } else if (entryPayload?.workType === 'Closed') {
              console.log('[Salesforce:Redux] Conversation work closed');
              dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.ENDED));
              // Also update the service's internal status
              salesforceService.setConversationStatus(CONVERSATION_STATUS.ENDED);
              
              // NOTE: Do NOT delete conversation or clear conversation ID
              // Keep conversation data for history/reference
              // User can manually clear it if needed via the clearChat action
              
              dispatch(setSalesforceTypingStatus({ isTyping: false }));
            } else {
              console.log('[Salesforce:Redux] Not starting timer - workType:', entryPayload?.workType);
            }
          } else {
            console.log('[Salesforce:Redux] No entryPayload found in event data');
          }
        } catch (error) {
          console.error('[Salesforce:Redux] Failed to parse CONVERSATION_ROUTING_WORK_RESULT:', error, event.data);
        }
      });

      salesforceService.addEventListener('CONVERSATION_TYPING_STARTED_INDICATOR', (event) => {
        try {
          const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

          // Only show typing indicator for agents, not end users
          const senderRole = eventData?.conversationEntry?.sender?.role;
          if (senderRole && senderRole !== 'EndUser') {
            dispatch(setSalesforceTypingStatus({
              isTyping: true,
              participantId: eventData.participantId,
              senderRole: senderRole
            }));
          }
        } catch (error) {
          console.error('[Salesforce:Redux] Failed to parse CONVERSATION_TYPING_STARTED_INDICATOR:', error, event.data);
        }
      });

      salesforceService.addEventListener('CONVERSATION_TYPING_STOPPED_INDICATOR', (event) => {
        try {
          const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

          // Only process typing stopped for agents, not end users
          const senderRole = eventData?.conversationEntry?.sender?.role;
          if (senderRole && senderRole !== 'EndUser') {
            dispatch(setSalesforceTypingStatus({
              isTyping: false,
              participantId: eventData.participantId,
              senderRole: senderRole
            }));
          }
        } catch (error) {
          console.error('[Salesforce:Redux] Failed to parse CONVERSATION_TYPING_STOPPED_INDICATOR:', error, event.data);
        }
      });

      salesforceService.addEventListener('CONVERSATION_CLOSE_CONVERSATION', (event) => {
        try {
          const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

          // ✅ FIX: Verify event is for CURRENT conversation (prevent old event replay)
          const currentConversationId = getConversationId();
          const eventConversationId = eventData?.conversationId;

          if (eventConversationId && currentConversationId && eventConversationId !== currentConversationId) {
            return; // Ignore events from old conversations
          }

          // Update conversation status to ended
          dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.ENDED));
          // Also update the service's internal status
          salesforceService.setConversationStatus(CONVERSATION_STATUS.ENDED);
          
          // NOTE: Do NOT delete conversation or clear conversation ID
          // Keep conversation data for history/reference
          // User can manually clear it if needed via the clearChat action

          // Clear any active typing indicators
          dispatch(setSalesforceTypingStatus({ isTyping: false }));
        } catch (error) {
          console.error('[Salesforce:Redux] Failed to parse CONVERSATION_CLOSE_CONVERSATION:', error, event.data);
        }
      });

      salesforceService.onConnectionStatusChange((status) => {
        dispatch(updateSalesforceConnectionStatus(status));
      });

      // Store user context in Redux for authenticated sessions
      if (userContext) {
        dispatch(setSalesforceUserContext(userContext));
      }

      // Get previousConversationId from state (set by restartConversation)
      // Extract _skipHistory flag before passing to service (it's not a routing attribute)
      const state = getState();
      const { _skipHistory, ...cleanRoutingAttributes } = routingAttributes || {};
      const skipHistory = _skipHistory === true;
      
      // If skipHistory is true, this is a fresh start - don't use previousConversationId
      // This makes it behave exactly like opening modal for the first time
      const previousConversationId = skipHistory ? null : state.supportMessageListRedux?.salesforce?.previousConversationId;
      
      if (previousConversationId) {
        console.log('[Salesforce:Redux] Using previousConversationId for new session:', previousConversationId);
      } else if (skipHistory) {
        console.log('[Salesforce:Redux] Fresh start requested - creating new conversation (like first modal open)');
      }

      const result = await salesforceService.startConversation(userContext, cleanRoutingAttributes, previousConversationId, skipHistory);
      
      // If we got existing messages (cross-device continuity), add them to Redux
      if (result.existingMessages && result.existingMessages.length > 0) {
        console.log('[Salesforce:Redux] Loading', result.existingMessages.length, 'existing messages from conversation history');
        // Add all restored messages at once
        // Use handleSalesforceMessage but ensure isRestored flag is set
        result.existingMessages.forEach(message => {
          dispatch(handleSalesforceMessage({ ...message, isRestored: true }));
        });
      }
      
      // ✅ FIX: Start timer if conversation is waiting for agent (page reload scenario)
      if (result.waitingForAgent) {
        console.log('[Salesforce:Redux] 🔥 Conversation restored in WAITING_FOR_AGENT state - starting timer');
        // Use setTimeout to ensure reducer completes before starting timer
        setTimeout(() => {
          dispatch(startAgentWaitTimerAsync({ timeout: 30 }));
        }, 100);
      }
      
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const resumeSalesforceConversation = createAsyncThunk(
  'supportMessageListRedux/resumeSalesforceConversation',
  async (conversationId, { rejectWithValue, dispatch, getState }) => {
    try {
      // STEP 1: Get continuation JWT (refresh existing JWT)
      const { getContinuityJwt } = await import('../services/authService');

      try {
        await getContinuityJwt();
      } catch (jwtError) {
        console.error('[Salesforce:Redux] Failed to get continuation JWT:', jwtError);
        // If JWT refresh fails, we can't resume
        return rejectWithValue({
          error: 'JWT_REFRESH_FAILED',
          message: 'Failed to refresh JWT for conversation resume',
          shouldStartNew: true
        });
      }

      // STEP 2: Check if conversation is still active via service
      const isActive = await salesforceService.checkConversationStatus(conversationId);

      if (isActive) {
        // STEP 3: Fetch conversation history (CRITICAL!)
        const { getConversationEntries, transformConversationEntries, parseEntriesForRedux } =
          await import('../services/conversationService');

        let messages = null; // Changed from [] to null
        try {
          // Fetch entries with Message filter
          const entriesResponse = await getConversationEntries(conversationId, {
            limit: 50,
            entryTypeFilter: 'Message'
          });

          if (entriesResponse?.conversationEntries?.length > 0) {
            // Transform API format → SSE format
            const transformed = transformConversationEntries(
              entriesResponse.conversationEntries,
              conversationId
            );

            // Parse SSE format → Redux format
            messages = parseEntriesForRedux(transformed);
          } else {
            // API returned but no messages - set to empty array (not null)
            console.log('[Salesforce:Redux] Conversation history fetched but empty');
            messages = [];
          }
        } catch (historyError) {
          console.warn('[Salesforce:Redux] Failed to fetch conversation history:', historyError.message);
          // Don't fail the entire resume if history fetch fails
          messages = []; // Set to empty array on error
        }

        // Set up event listeners (same as startSalesforceConversation)
        salesforceService.addEventListener('CONVERSATION_MESSAGE', (event) => {
          try {
            const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

            let parsedMessage = eventData;
            if (eventData.conversationEntry && eventData.conversationEntry.entryPayload) {
              const entryPayload = JSON.parse(eventData.conversationEntry.entryPayload);
              parsedMessage = {
                ...eventData,
                messageText: entryPayload.abstractMessage?.staticContent?.text || '',
                text: entryPayload.abstractMessage?.staticContent?.text || '',
                senderRole: eventData.conversationEntry?.sender?.role,
                actorType: eventData.conversationEntry?.sender?.role,
                messageId: eventData.conversationEntry?.identifier,
                timestamp: eventData.conversationEntry?.transcriptedTimestamp,
                conversationId: eventData.conversationId,
                entryType: entryPayload.entryType
              };
            }

            dispatch(handleSalesforceMessage(parsedMessage));
          } catch (error) {
            console.error('[Salesforce:Redux] Failed to parse CONVERSATION_MESSAGE (resumed):', error, event.data);
          }
        });

        salesforceService.addEventListener('CONVERSATION_ROUTING_WORK_RESULT', (event) => {
          try {
            const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

            // Check for work assignment/closure
            if (eventData?.conversationEntry?.entryPayload) {
              const entryPayload = JSON.parse(eventData.conversationEntry.entryPayload);

              if (entryPayload?.workType === 'Assigned') {
                // ✅ FIX: Only start timer if not already waiting (resumed conversation)
                const currentState = getState();
                const { agentWaitTimer } = currentState.supportMessageListRedux.salesforce;
                
                if (!agentWaitTimer.isWaiting && !agentWaitTimer.hasTimedOut) {
                  console.log('[Salesforce:Redux] Starting timer for resumed conversation - no existing timer');
                  dispatch(startAgentWaitTimerAsync({ timeout: 30 }));
                } else {
                  console.log('[Salesforce:Redux] Timer already running in resumed conversation - ignoring');
                }
              } else if (entryPayload?.workType === 'Declined') {
                console.log('[Salesforce:Redux] Agent declined resumed conversation - ignoring for now');
                // ✅ SIMPLIFIED: Ignore all decline events for now  
                // The modal reopen logic will handle restarting timers when needed
                console.log('[Salesforce:Redux] Decline events are ignored - modal reopen logic handles timer management');
              } else if (entryPayload?.workType === 'Closed') {
                dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.ENDED));
                // Also update the service's internal status
                salesforceService.setConversationStatus(CONVERSATION_STATUS.ENDED);
                // Clear stored conversation ID since conversation is ended
                salesforceService.clearConversationId();
                dispatch(setSalesforceTypingStatus({ isTyping: false }));
              }
            }
          } catch (error) {
            console.error('[Salesforce:Redux] Failed to parse CONVERSATION_ROUTING_WORK_RESULT (resumed):', error, event.data);
          }
        });

        salesforceService.addEventListener('CONVERSATION_PARTICIPANT_CHANGED', (event) => {
          try {
            const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

            // Check participant changes for both agent joining and leaving
            if (eventData?.conversationEntry?.entryPayload) {
              const entryPayload = JSON.parse(eventData.conversationEntry.entryPayload);

              if (entryPayload?.entries) {
                // Check for agent additions (agent accepting conversation)
                const agentAdditions = entryPayload.entries.filter(entry =>
                  entry.operation === 'add' &&
                  entry.participant?.role === 'Agent'
                );

                // Check for agent removals (conversation end)
                const agentRemovals = entryPayload.entries.filter(entry =>
                  entry.operation === 'remove' &&
                  entry.participant?.role === 'Agent'
                );

                if (agentAdditions.length > 0) {
                  // ✅ Agent has joined the conversation (resumed flow)!
                  console.log('[Salesforce:Redux] 🎉 Agent joined (resumed)! Stopping timer and activating conversation');
                  // Store agent name for later use in footer
                  const agentName = agentAdditions[0]?.displayName || null;
                  if (agentName) {
                    dispatch(setAgentName(agentName));
                  }
                  // Extract the actual timestamp from the event
                  let agentJoinedTimestamp = extractEventDataTimestamp(eventData);
                  dispatch(agentJoinedConversation(agentJoinedTimestamp));
                } else if (agentRemovals.length > 0) {
                  // Agent left - end conversation and delete it from server
                  dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.ENDED));
                  // Also update the service's internal status
                  salesforceService.setConversationStatus(CONVERSATION_STATUS.ENDED);

                  // Clear stored conversation ID since conversation is ended
                  salesforceService.clearConversationId();
                  // Clear any active typing indicators
                  dispatch(setSalesforceTypingStatus({ isTyping: false }));
                }
              }
            }

            dispatch(updateSalesforceParticipant(eventData));
          } catch (error) {
            console.error('[Salesforce:Redux] Failed to parse CONVERSATION_PARTICIPANT_CHANGED (resumed):', error, event.data);
          }
        });

        salesforceService.addEventListener('CONVERSATION_TYPING_STARTED_INDICATOR', (event) => {
          try {
            const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

            // Only show typing indicator for agents, not end users
            const senderRole = eventData?.conversationEntry?.sender?.role;
            if (senderRole && senderRole !== 'EndUser') {
              dispatch(setSalesforceTypingStatus({
                isTyping: true,
                participantId: eventData.participantId,
                senderRole: senderRole
              }));
            }
          } catch (error) {
            console.error('[Salesforce:Redux] Failed to parse CONVERSATION_TYPING_STARTED_INDICATOR:', error, event.data);
          }
        });

        salesforceService.addEventListener('CONVERSATION_TYPING_STOPPED_INDICATOR', (event) => {
          try {
            const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

            // Only process typing stopped for agents, not end users
            const senderRole = eventData?.conversationEntry?.sender?.role;
            if (senderRole && senderRole !== 'EndUser') {
              dispatch(setSalesforceTypingStatus({
                isTyping: false,
                participantId: eventData.participantId,
                senderRole: senderRole
              }));
            }
          } catch (error) {
            console.error('[Salesforce:Redux] Failed to parse CONVERSATION_TYPING_STOPPED_INDICATOR:', error, event.data);
          }
        });

        salesforceService.addEventListener('CONVERSATION_CLOSE_CONVERSATION', (event) => {
          try {
            const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

            // ✅ FIX: Verify event is for CURRENT conversation (prevent old event replay)
            const currentConversationId = getConversationId();
            const eventConversationId = eventData?.conversationId;

            if (eventConversationId && currentConversationId && eventConversationId !== currentConversationId) {
              return; // Ignore events from old conversations
            }

            // Update conversation status to ended
            dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.ENDED));
            // Also update the service's internal status
            salesforceService.setConversationStatus(CONVERSATION_STATUS.ENDED);

            // Clear stored conversation ID (this also closes EventSource automatically)
            salesforceService.clearConversationId();

            // Clear any active typing indicators
            dispatch(setSalesforceTypingStatus({ isTyping: false }));
          } catch (error) {
            console.error('[Salesforce:Redux] Failed to parse CONVERSATION_CLOSE_CONVERSATION (resumed):', error, event.data);
          }
        });

        // ✅ CRITICAL: Set up connection status change callback
        // This will update Redux state when EventSource actually connects
        salesforceService.onConnectionStatusChange((status) => {
          dispatch(updateSalesforceConnectionStatus(status));
        });

        // Resume the existing conversation
        const result = await salesforceService.resumeConversation(conversationId);
        return { ...result, isResumed: true, messages };
      } else {
        // If conversation is no longer active, clear stored ID and start new one
        await salesforceService.clearConversationId();
        throw new Error('CONVERSATION_EXPIRED');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendSalesforceMessage = createAsyncThunk(
  'supportMessageListRedux/sendSalesforceMessage',
  async (messageText, { rejectWithValue }) => {
    try {
      const result = await salesforceService.sendMessage(messageText);
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendSalesforceTypingIndicator = createAsyncThunk(
  'supportMessageListRedux/sendSalesforceTypingIndicator',
  async (isTyping = true, { rejectWithValue }) => {
    try {
      await salesforceService.sendTypingIndicator(isTyping);
      return isTyping;
    } catch (error) {
      // Typing indicators are not critical - don't show errors to user
      console.debug('[Salesforce:Redux] Typing indicator failed:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const endSalesforceConversation = createAsyncThunk(
  'supportMessageListRedux/endSalesforceConversation',
  async (_, { rejectWithValue }) => {
    try {
      await salesforceService.endConversation();
      return true;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const shutdownSalesforce = createAsyncThunk(
  'supportMessageListRedux/shutdownSalesforce',
  async (_, { rejectWithValue }) => {
    try {
      await salesforceService.shutdown();
      return true;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Agent Wait Timer Management
export const startAgentWaitTimerAsync = createAsyncThunk(
  'supportMessageListRedux/startAgentWaitTimer',
  async ({ timeout = 30 } = {}, { dispatch, getState }) => {
    console.log('[Salesforce:Redux] 🔥 startAgentWaitTimerAsync called with timeout:', timeout);
    
    // ✅ SAFEGUARD: Clear any existing timers first
    const currentState = getState();
    const { agentWaitTimer } = currentState.supportMessageListRedux.salesforce;
    if (agentWaitTimer.timeoutId) {
      console.log('[Salesforce:Redux] Clearing existing timer before starting new one');
      clearInterval(agentWaitTimer.timeoutId);
    }
    
    dispatch(startAgentWaitTimer({ timeout }));
    
    // Start countdown timer
    const startCountdown = () => {
      let remaining = timeout;
      console.log('[Salesforce:Redux] Starting countdown from:', remaining);
      
      const countdownInterval = setInterval(() => {
        const state = getState();
        const { isNetworkOnline } = state.supportMessageListRedux.salesforce;
        if (!isNetworkOnline) {
          console.log('[Salesforce:Redux] Offline, skipping decrement');
          return;
        }

        remaining -= 1;
        console.log('[Salesforce:Redux] Timer countdown:', remaining);
        dispatch(updateAgentWaitTimer({ remainingTime: remaining }));
        
        if (remaining <= 0) {
          console.log('[Salesforce:Redux] Timer expired!');
          clearInterval(countdownInterval);
          
          // Get current state to check if this is first or second timeout
          const state = getState();
          const { conversationStatus, agentWaitTimer } = state.supportMessageListRedux.salesforce;
          
          // If WAITING_FOR_AGENT, transition to AGENT_UNAVAILABLE and start second timer
          if (conversationStatus === CONVERSATION_STATUS.WAITING_FOR_AGENT && !agentWaitTimer.hasTimedOut) {
            console.log('[Salesforce:Redux] First timeout (30s) - starting second countdown');
            dispatch(agentWaitTimeout());
            // Start second 120s countdown
            dispatch(startAgentWaitTimerAsync({ timeout: 120 }));
          } 
          // If AGENT_UNAVAILABLE, transition to NO_AGENTS_FOUND
          else if (conversationStatus === CONVERSATION_STATUS.AGENT_UNAVAILABLE && !agentWaitTimer.secondTimeout) {
            console.log('[Salesforce:Redux] Second timeout (60s total) - no agents available');
            dispatch(agentSecondTimeout());
          }
        }
      }, 1000);
      
      // Store interval ID in a way that can be cleared
      return countdownInterval;
    };
    
    const intervalId = startCountdown();
    
    // Return the interval ID so it can be stored and cleared later
    return { intervalId, timeout };
  }
);

export const stopAgentWaitTimerAsync = createAsyncThunk(
  'supportMessageListRedux/stopAgentWaitTimer',
  async (_, { dispatch, getState }) => {
    const state = getState();
    const { agentWaitTimer } = state.supportMessageListRedux.salesforce;
    
    // Clear any existing timer
    if (agentWaitTimer.timeoutId) {
      clearInterval(agentWaitTimer.timeoutId);
    }
    
    dispatch(stopAgentWaitTimer());
    return true;
  }
);

const supportMessageListRedux = createSlice({
  name: 'supportMessageListRedux',
  initialState,
  reducers: {
    // Existing actions
    chatMessageSend: (state, action) => {
      state.items = [...state.items, action.payload];
    },
    chatMessageUpdateStatus: (state, action) => {
      const { ids, status } = action.payload;
      state.items = state.items.map(item =>
        ids.includes(item.id) ? { ...item, status } : item
      );
    },
    setChatUserWriting: (state, action) => {
      // Handle typing status if needed
    },
    closeChat: (state) => {
      state.openModal = false;
    },
    openChat: (state) => {
      state.openModal = true;
    },
    setChat: (state, action) => {
      state.chat = action.payload;
    },
    setItems: (state, action) => {
      state.items = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Salesforce-specific actions
    updateSalesforceStatus: (state, action) => {
      const status = salesforceService.getStatus();
      const oldConversationStatus = state.salesforce.conversationStatus;
      
      // ✅ FIX: Don't override conversation status during agent wait timer or when ACTIVE
      // ✅ BUT: Allow fresh attempts when timer has been reset (hasTimedOut = false)
      // ✅ ALSO: Don't preserve ENDED status - always allow transition from ENDED
      // ✅ Preserve NO_AGENTS_FOUND and ERROR status until user takes action
      const shouldPreserveStatus = (
        state.salesforce.agentWaitTimer.isWaiting || 
        oldConversationStatus === CONVERSATION_STATUS.WAITING_FOR_AGENT ||
        oldConversationStatus === CONVERSATION_STATUS.ACTIVE ||
        oldConversationStatus === CONVERSATION_STATUS.NO_AGENTS_FOUND ||
        oldConversationStatus === CONVERSATION_STATUS.ERROR ||
        (oldConversationStatus === CONVERSATION_STATUS.AGENT_UNAVAILABLE && state.salesforce.agentWaitTimer.hasTimedOut)
        // Note: ENDED status is NOT preserved - it should transition to new states
      );
      
      if (shouldPreserveStatus) {
        console.log('[Salesforce:Redux] 🔄 updateSalesforceStatus - Preserving conversation status:', oldConversationStatus);
        console.log('[Salesforce:Redux] 🔄 updateSalesforceStatus - Service status would be:', status.conversationStatus);
        // Update everything EXCEPT conversationStatus
        const { conversationStatus, ...statusWithoutConversationStatus } = status;
        state.salesforce = {
          ...state.salesforce,
          ...statusWithoutConversationStatus,
          conversationStatus: oldConversationStatus, // Keep our status
          lastEventTimestamp: Date.now()
        };
      } else {
        // Normal update when not waiting for agent OR when starting fresh after reset
        console.log('[Salesforce:Redux] 🔄 updateSalesforceStatus - Normal update, status:', oldConversationStatus, '→', status.conversationStatus);
        state.salesforce = {
          ...state.salesforce,
          ...status,
          lastEventTimestamp: Date.now()
        };
      }
    },
    updateSalesforceConnectionStatus: (state, action) => {
      state.salesforce.connectionStatus = action.payload;
      state.salesforce.isEventSourceConnected = action.payload === CONNECTION_STATUS.CONNECTED;
    },
    setSalesforceSessionInitState: (state, action) => {
      // Update session initialization state: 'not-started' | 'initializing' | 'initialized'
      state.salesforce.sessionInitState = action.payload;
    },
    setSalesforceConversationId: (state, action) => {
      state.salesforce.conversationId = action.payload;
    },
    setSalesforceConversationStatus: (state, action) => {
      const oldStatus = state.salesforce.conversationStatus;
      const newStatus = action.payload;
      
      // Special alert for status changes during timer countdown
      if (state.salesforce.agentWaitTimer.isWaiting && oldStatus === 'WAITING_FOR_AGENT' && newStatus !== 'WAITING_FOR_AGENT') {
        console.error(`[Salesforce:Redux] ⚠️  CRITICAL: Status changed from WAITING_FOR_AGENT to ${newStatus} while timer is active!`);
        console.error('[Salesforce:Redux] ⚠️  This will cause the timeout UI to clear!');
        console.error('[Salesforce:Redux] ⚠️  Call stack:', new Error().stack);
      }
      
      // Debug ALL status changes during timer countdown
      if (state.salesforce.agentWaitTimer.isWaiting) {
        console.warn(`[Salesforce:Redux] 🚨 STATUS CHANGE DURING TIMER: ${oldStatus} → ${newStatus}`);
        console.warn('[Salesforce:Redux] 🚨 Timer is running, status change may affect UI');
        console.warn('[Salesforce:Redux] 🚨 Call stack:', new Error().stack);
      }
      
      console.log(`[Salesforce:Redux] 📝 setSalesforceConversationStatus called - changing from ${oldStatus} to ${newStatus}`);
      console.log('[Salesforce:Redux] � setSalesforceConversationStatus stack trace:', new Error().stack);
      state.salesforce.conversationStatus = action.payload;
    },
    handleSalesforceMessage: (state, action) => {
      const message = action.payload;

      // Skip empty messages
      if (!message.messageText && !message.text) {
        return;
      }

      // Determine if message is from end user or agent
      const isEndUserMessage = message.isEndUserMessage ||
        message.senderRole === 'EndUser' ||
        message.actorType === 'EndUser';

      // Handle cross-device sync and prevent duplicates
      // Strategy: Check if message already exists, if so update it, otherwise add it
      // Allow restored messages (from history) to include all EndUser messages
      const messageId = message.id || message.messageId;
      const wasSentFromThisDevice = state.salesforce.sentMessageIds.includes(messageId);
      
      // Check if this message already exists in the items array
      const existingMessageIndex = state.items.findIndex(item => 
        item.id === messageId || (item.id && item.id.startsWith('temp_') && item.text === (message.messageText || message.text))
      );
      
      if (existingMessageIndex !== -1) {
        // Message already exists (optimistic UI) - update it with server data
        console.log('[Salesforce:Redux] Updating existing message:', messageId);
        const formattedMessage = {
          ...state.items[existingMessageIndex],
          id: messageId,
          text: message.messageText || message.text,
          messageText: message.messageText || message.text,
          timestamp: message.timestamp || message.transcriptedTimestamp || state.items[existingMessageIndex].timestamp,
          senderRole: message.senderRole,
          actorType: message.actorType,
          status: 'received',
          ...message
        };
        state.items[existingMessageIndex] = formattedMessage;
        return;
      }
      
      // For EndUser messages, skip only if sent from this device AND message wasn't found above
      // This handles the edge case where EventSource arrives before API response
      if (isEndUserMessage && !message.isRestored && wasSentFromThisDevice) {
        console.log('[Salesforce:Redux] Skipping EndUser message sent from this device (no existing message found):', messageId);
        return;
      }
      
      // Log cross-device messages for debugging
      if (isEndUserMessage && !message.isRestored && !wasSentFromThisDevice) {
        console.log('[Salesforce:Redux] Receiving EndUser message from another device:', messageId);
      }

      // Add new Salesforce message to items array with proper formatting
      const formattedMessage = {
        id: message.id || message.messageId || `salesforce_${Date.now()}`,
        text: message.messageText || message.text,
        messageText: message.messageText || message.text,
        timestamp: message.timestamp || message.transcriptedTimestamp || Date.now(),
        sender: isEndUserMessage ? 'user' : 'agent',
        senderRole: message.senderRole,
        actorType: message.actorType,
        isEndUserMessage,
        type: 'salesforce',
        conversationId: state.salesforce.conversationId,
        status: 'received',
        isRestored: message.isRestored || false,
        ...message
      };

      state.items = [...state.items, formattedMessage];
    },
    addRestoredMessages: (state, action) => {
      // Add restored messages (from conversation history) to the store
      // This includes BOTH EndUser and Agent messages
      const messages = action.payload;
      
      if (!Array.isArray(messages) || messages.length === 0) {
        return;
      }
      
      console.log('[Salesforce:Redux] Adding', messages.length, 'restored messages to store');
      
      const formattedMessages = messages.map(message => {
        const isEndUserMessage = message.isEndUserMessage ||
          message.senderRole === 'EndUser' ||
          message.actorType === 'EndUser';
        
        return {
          id: message.id || message.messageId || `salesforce_restored_${Date.now()}_${Math.random()}`,
          text: message.messageText || message.text || message.message,
          messageText: message.messageText || message.text || message.message,
          timestamp: message.timestamp || message.transcriptedTimestamp || Date.now(),
          sender: isEndUserMessage ? 'user' : 'agent',
          senderRole: message.senderRole,
          senderDisplayName: message.senderDisplayName,
          actorType: message.actorType,
          isEndUserMessage,
          type: 'salesforce',
          conversationId: state.salesforce.conversationId || message.conversationId,
          status: 'received',
          isRestored: true,
          ...message
        };
      });
      
      // Add all restored messages at once
      state.items = [...state.items, ...formattedMessages];
    },
    setAgentName: (state, action) => {
      // Store agent name when agent joins the conversation
      state.salesforce.agentName = action.payload;
    },
    setSalesforceUserContext: (state, action) => {
      // Store user context for authenticated sessions (userId, email, name)
      state.salesforce.userContext = action.payload;
    },
    updateSalesforceParticipant: (state, action) => {
      const participantData = action.payload;
      // Ensure we store an object, not a string
      if (typeof participantData === 'string') {
        try {
          state.salesforce.participantInfo = JSON.parse(participantData);
        } catch (error) {
          console.error('Failed to parse participant data as JSON:', error, participantData);
          state.salesforce.participantInfo = { rawData: participantData };
        }
      } else {
        state.salesforce.participantInfo = participantData;
      }
    },
    setSalesforceTypingStatus: (state, action) => {
      const { isTyping, participantId, senderRole } = action.payload;
      // Update typing status for specific participant
      // Ensure participantInfo is an object, not a string
      if (state.salesforce.participantInfo && typeof state.salesforce.participantInfo === 'object') {
        state.salesforce.participantInfo.isTyping = isTyping;
        state.salesforce.participantInfo.senderRole = senderRole;
      } else {
        // Initialize participantInfo if it doesn't exist or is not an object
        state.salesforce.participantInfo = {
          isTyping,
          participantId: participantId || 'unknown',
          senderRole: senderRole || 'unknown'
        };
      }
    },
    setSalesforceRoutingAttributes: (state, action) => {
      state.salesforce.routingAttributes = action.payload;
    },
    clearSalesforceData: (state) => {
      state.salesforce = {
        ...initialState.salesforce
      };
      // Optionally clear Salesforce messages
      state.items = state.items.filter(item => item.type !== 'salesforce');
    },
    resetConversation: (state) => {
      // Reset conversation state completely for a fresh start
      state.salesforce = {
        ...initialState.salesforce,
        isInitialized: state.salesforce.isInitialized, // Keep initialization status
        isServiceReady: state.salesforce.isServiceReady, // Keep service ready status
        sentMessageIds: [] // Clear sent message tracking
      };
      // Clear all messages (set to null to prevent flashing)
      state.items = null;
      // Clear any error states
      state.error = null;
      state.loading = false;
      // Reset modal state
      state.openModal = false;
      // Reset conversations count so fresh start shows waiting screen
      state.conversationsCount = 0;

      // Clear salesforceService state and close connections (enhanced for fresh start)
      // Note: clearConversationId() also closes EventSource automatically
      salesforceService.clearConversationId();
      salesforceService.setConversationStatus(CONVERSATION_STATUS.NOT_STARTED);

      // Clear Last-Event-ID to prevent historical event replay (KEY FIX!)
      try {
        clearLastEventId();
        clearConversationStartTime();
      } catch (error) {
        console.warn('Failed to clear Last-Event-ID during reset:', error);
      }

      // Set flag for fresh connection next time
      salesforceService.needsFreshConnection = true;
    },
    startAgentWaitTimer: (state, action) => {
      const { timeout = 30 } = action.payload || {};
      console.log('[Salesforce:Redux] 🔥 startAgentWaitTimer reducer called with timeout:', timeout);
      console.log('[Salesforce:Redux] 🔥 Status before starting timer:', state.salesforce.conversationStatus);
      
      // Clear any existing timer
      if (state.salesforce.agentWaitTimer.timeoutId) {
        clearTimeout(state.salesforce.agentWaitTimer.timeoutId);
      }
      
      state.salesforce.agentWaitTimer = {
        isWaiting: true,
        timeoutId: null, // Will be set by the async thunk
        startTime: Date.now(),
        remainingTime: timeout,
        hasTimedOut: state.salesforce.agentWaitTimer.hasTimedOut, // Preserve hasTimedOut flag
        secondTimeout: state.salesforce.agentWaitTimer.secondTimeout // Preserve secondTimeout flag
      };
      
      // Only set status to WAITING_FOR_AGENT if this is the first timer (not during AGENT_UNAVAILABLE)
      if (state.salesforce.conversationStatus !== CONVERSATION_STATUS.AGENT_UNAVAILABLE) {
        console.log('[Salesforce:Redux] 🔥 startAgentWaitTimer - Setting status from', state.salesforce.conversationStatus, 'to WAITING_FOR_AGENT');
        state.salesforce.conversationStatus = CONVERSATION_STATUS.WAITING_FOR_AGENT;
        console.log('[Salesforce:Redux] 🔥 Conversation status set to WAITING_FOR_AGENT');
      } else {
        console.log('[Salesforce:Redux] 🔥 startAgentWaitTimer - Keeping AGENT_UNAVAILABLE status for second countdown');
      }
      console.log('[Salesforce:Redux] 🔥 Status after starting timer:', state.salesforce.conversationStatus);
    },
    updateAgentWaitTimer: (state, action) => {
      const { remainingTime } = action.payload;
      // Allow timer updates if waiting and not reached second timeout
      if (state.salesforce.agentWaitTimer.isWaiting && !state.salesforce.agentWaitTimer.secondTimeout) {
        state.salesforce.agentWaitTimer.remainingTime = remainingTime;
      }
    },
    stopAgentWaitTimer: (state) => {
      if (state.salesforce.agentWaitTimer.timeoutId) {
        console.log('[Salesforce:Redux] ⏹️ stopAgentWaitTimer - Clearing timer interval:', state.salesforce.agentWaitTimer.timeoutId);
        clearInterval(state.salesforce.agentWaitTimer.timeoutId);
      }
      
      state.salesforce.agentWaitTimer = {
        isWaiting: false,
        timeoutId: null,
        startTime: null,
        remainingTime: 30,
        hasTimedOut: false,
        secondTimeout: false
      };
    },
    agentWaitTimeout: (state) => {
      console.log('[Salesforce:Redux] 🔥 AGENT WAIT TIMEOUT - First timeout (30s)');
      console.log('[Salesforce:Redux] 🔥 Status before timeout:', state.salesforce.conversationStatus);
      console.log('[Salesforce:Redux] 🔥 Timer state before timeout:', JSON.stringify(state.salesforce.agentWaitTimer, null, 2));
      state.salesforce.agentWaitTimer.hasTimedOut = true;
      // Keep isWaiting true to allow second countdown
      state.salesforce.agentWaitTimer.isWaiting = true;
      state.salesforce.agentWaitTimer.remainingTime = 30;
      console.log('[Salesforce:Redux] 🔥 agentWaitTimeout - Setting status from', state.salesforce.conversationStatus, 'to AGENT_UNAVAILABLE');
      state.salesforce.conversationStatus = CONVERSATION_STATUS.AGENT_UNAVAILABLE;
      console.log('[Salesforce:Redux] 🔥 Conversation status set to AGENT_UNAVAILABLE');
      console.log('[Salesforce:Redux] 🔥 Status after timeout:', state.salesforce.conversationStatus);
    },
    agentSecondTimeout: (state) => {
      console.log('[Salesforce:Redux] 🔥 AGENT SECOND TIMEOUT - Second timeout (60s total)');
      console.log('[Salesforce:Redux] 🔥 Status before second timeout:', state.salesforce.conversationStatus);
      state.salesforce.agentWaitTimer.secondTimeout = true;
      state.salesforce.agentWaitTimer.isWaiting = false;
      console.log('[Salesforce:Redux] 🔥 agentSecondTimeout - Setting status from', state.salesforce.conversationStatus, 'to NO_AGENTS_FOUND');
      state.salesforce.conversationStatus = CONVERSATION_STATUS.NO_AGENTS_FOUND;
      console.log('[Salesforce:Redux] 🔥 Conversation status set to NO_AGENTS_FOUND');
      
      // Close the conversation and clear stored data
      salesforceService.clearConversationId();
      salesforceService.setConversationStatus(CONVERSATION_STATUS.NO_AGENTS_FOUND);
      
      // Clear Last-Event-ID and conversation start time from localStorage
      try {
        clearLastEventId();
        clearConversationStartTime();
        console.log('[Salesforce:Redux] 🔥 Cleared localStorage data for NO_AGENTS_FOUND');
      } catch (error) {
        console.warn('[Salesforce:Redux] Failed to clear localStorage during NO_AGENTS_FOUND:', error);
      }
      
      console.log('[Salesforce:Redux] 🔥 Status after second timeout and cleanup:', state.salesforce.conversationStatus);
    },
    resetAgentWaitState: (state) => {
      // Reset wait state when modal is closed/reopened to show fresh timer
      // Handle timeout, ended conversation, and ERROR scenarios
      const currentStatus = state.salesforce.conversationStatus;
      const needsReset = (
        state.salesforce.agentWaitTimer.hasTimedOut || 
        state.salesforce.agentWaitTimer.secondTimeout ||
        // currentStatus === CONVERSATION_STATUS.ENDED ||
        currentStatus === CONVERSATION_STATUS.AGENT_UNAVAILABLE ||
        currentStatus === CONVERSATION_STATUS.NO_AGENTS_FOUND ||
        currentStatus === CONVERSATION_STATUS.ERROR
      );
      
      if (needsReset) {
        console.log('[Salesforce:Redux] Reset agent wait state and conversation status for fresh start');
        console.log('[Salesforce:Redux] Reset reason - hasTimedOut:', state.salesforce.agentWaitTimer.hasTimedOut, 'secondTimeout:', state.salesforce.agentWaitTimer.secondTimeout, 'status:', currentStatus);
        
        // ✅ CRITICAL: Clear any running timer interval BEFORE resetting state
        if (state.salesforce.agentWaitTimer.timeoutId) {
          console.log('[Salesforce:Redux] Clearing running timer interval:', state.salesforce.agentWaitTimer.timeoutId);
          clearInterval(state.salesforce.agentWaitTimer.timeoutId);
        }
        
        // ✅ For ERROR state, clear the stored conversation ID and messages FIRST
        const wasError = currentStatus === CONVERSATION_STATUS.ERROR;
        if (wasError) {
          console.log('[Salesforce:Redux] ERROR state detected - clearing conversation ID and messages');
          salesforceService.clearConversationId();
          state.salesforce.conversationId = null;
          state.items = null;
          state.error = null;
          state.salesforce.agentName = null;
          
          // Clear Last-Event-ID to prevent replay
          try {
            clearLastEventId();
            clearConversationStartTime();
          } catch (error) {
            console.warn('[Salesforce:Redux] Failed to clear Last-Event-ID during ERROR reset:', error);
          }
        }
        
        // ✅ Complete reset of timer state
        state.salesforce.agentWaitTimer = {
          isWaiting: false,
          timeoutId: null,
          startTime: null,
          remainingTime: 30,
          hasTimedOut: false,
          secondTimeout: false
        };
        
        // ✅ Reset conversation status to allow fresh conversation attempt
        // Force to NOT_STARTED to ensure clean state and trigger new flow
        state.salesforce.conversationStatus = CONVERSATION_STATUS.NOT_STARTED;
        console.log('[Salesforce:Redux] Conversation status reset to NOT_STARTED for fresh attempt');

        // ✅ CRITICAL: Also reset salesforceService internal state to prevent stale status from polluting Redux
        // The 3-second polling (updateSalesforceStatus) reads from salesforceService.getStatus()
        // If we don't reset the service, it will keep returning old CONNECTING status
        salesforceService.setConversationStatus(CONVERSATION_STATUS.NOT_STARTED);

        // ✅ Clear any lingering timeout UI by ensuring clean state
        // This ensures updateSalesforceStatus won't preserve AGENT_UNAVAILABLE or ENDED
        console.log('[Salesforce:Redux] Fresh attempt state prepared - ready for new timer');
      } else {
        console.log('[Salesforce:Redux] Reset not needed - status:', state.salesforce.conversationStatus, 'hasTimedOut:', state.salesforce.agentWaitTimer.hasTimedOut);
      }
    },
    agentJoinedConversation: (state, action) => {
      const agentJoinedTimestamp = action.payload;
      console.log('[Salesforce:Redux] 👤 agentJoinedConversation - Status before:', state.salesforce.conversationStatus);
      // Agent has joined - stop timer and set active
      if (state.salesforce.agentWaitTimer.timeoutId) {
        console.log('[Salesforce:Redux] 👤 agentJoinedConversation - Clearing timer interval:', state.salesforce.agentWaitTimer.timeoutId);
        clearInterval(state.salesforce.agentWaitTimer.timeoutId);
      }
      
      state.salesforce.agentWaitTimer = {
        isWaiting: false,
        timeoutId: null,
        startTime: null,
        remainingTime: 30,
        hasTimedOut: false,
        secondTimeout: false
      };
      
      console.log('[Salesforce:Redux] 👤 agentJoinedConversation - Setting status from', state.salesforce.conversationStatus, 'to ACTIVE');
      state.salesforce.conversationStatus = CONVERSATION_STATUS.ACTIVE;
      state.salesforce.hasAgentJoined = true;
      // Set timestamp from event for accurate positioning of "agent joined" message
      // Only accept timestamp from agent joined event - ignore work routed success event
      if (agentJoinedTimestamp) {
        state.salesforce.agentJoinedAt = agentJoinedTimestamp;
      }
      console.log('[Salesforce:Redux] 👤 agentJoinedConversation - Status set to ACTIVE, hasAgentJoined set to true, agentJoinedAt:', state.salesforce.agentJoinedAt);
    },
    setIsNetworkOnline: (state, action) => {
      state.salesforce.isNetworkOnline = action.payload;
      console.log('[Salesforce:Redux] Network status updated:', action.payload ? 'ONLINE' : 'OFFLINE');
    },
    cancelConversationRequest: (state) => {
      console.log('[Salesforce:Redux] ❌ cancelConversationRequest - Canceling conversation request');
      
      // Stop any running timer
      if (state.salesforce.agentWaitTimer.timeoutId) {
        console.log('[Salesforce:Redux] ❌ Clearing timer interval:', state.salesforce.agentWaitTimer.timeoutId);
        clearInterval(state.salesforce.agentWaitTimer.timeoutId);
      }
      
      // Reset conversation state completely for a fresh start
      state.salesforce = {
        ...initialState.salesforce,
        isInitialized: state.salesforce.isInitialized, // Keep initialization status
        isServiceReady: state.salesforce.isServiceReady, // Keep service ready status
        sentMessageIds: [] // Clear sent message tracking
      };
      
      // Clear all messages
      state.items = [];
      
      // Clear any error states
      state.error = null;
      state.loading = false;
      
      // Close modal
      state.openModal = false;

      // Clear salesforceService state and close connections
      salesforceService.clearConversationId();
      salesforceService.setConversationStatus(CONVERSATION_STATUS.NOT_STARTED);

      // Clear Last-Event-ID to prevent historical event replay
      try {
        clearLastEventId();
        clearConversationStartTime();
      } catch (error) {
        console.warn('[Salesforce:Redux] Failed to clear Last-Event-ID during cancel:', error);
      }

      // Set flag for fresh connection next time
      salesforceService.needsFreshConnection = true;
      
      console.log('[Salesforce:Redux] ❌ Conversation request canceled and reset to initial state');
    },
    restartConversation: (state) => {
      console.log('[Salesforce:Redux] 🔄 restartConversation - Restarting without closing modal');
      
      // Stop any running timer
      if (state.salesforce.agentWaitTimer.timeoutId) {
        console.log('[Salesforce:Redux] 🔄 Clearing timer interval:', state.salesforce.agentWaitTimer.timeoutId);
        clearInterval(state.salesforce.agentWaitTimer.timeoutId);
      }
      
      // IMPORTANT: Store the old conversationId before resetting
      const oldConversationId = state.salesforce.conversationId;
      console.log('[Salesforce:Redux] 🔄 Storing old conversationId for new session:', oldConversationId);
      
      // Reset conversation state but keep modal open
      state.salesforce = {
        ...initialState.salesforce,
        isInitialized: state.salesforce.isInitialized, // Keep initialization status
        isServiceReady: state.salesforce.isServiceReady, // Keep service ready status
        sessionInitState: 'initialized', // ✅ FIX: Keep session initialized for back button flow
        previousConversationId: oldConversationId, // Store old ID for creating new session
        sentMessageIds: [] // Clear sent message tracking for fresh conversation
      };
      
      // Clear all messages (set to null to prevent flashing)
      state.items = null;
      
      // Clear any error states
      state.error = null;
      state.loading = false;
      
      // Keep modal OPEN (different from cancelConversationRequest)
      // state.openModal stays as-is

      // NOTE: Do NOT delete conversation or clear conversationId yet
      // The old conversationId will be used when creating the new session
      
      salesforceService.setConversationStatus(CONVERSATION_STATUS.NOT_STARTED);

      // Clear Last-Event-ID to prevent historical event replay
      try {
        clearLastEventId();
        clearConversationStartTime();
      } catch (error) {
        console.warn('[Salesforce:Redux] Failed to clear Last-Event-ID during restart:', error);
      }

      // Set flag for fresh connection next time
      salesforceService.needsFreshConnection = true;
      
      console.log('[Salesforce:Redux] 🔄 Conversation restarted, modal remains open');
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetALL, () => initialState)

      // Handle logout via CLEAR_REDUX
      .addCase(CLEAR_REDUX, (state, action) => {
        // Reset AI chat state when clearRedux("") is called during logout
        if (action.payload?.option === "" || !action.payload?.option) {
          return initialState;
        }
        return state;
      })

      // Initialize Salesforce
      .addCase(initializeSalesforce.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeSalesforce.fulfilled, (state, action) => {
        state.loading = false;
        state.salesforce.isInitialized = true;
        state.salesforce.isServiceReady = salesforceService.isReady();
      })
      .addCase(initializeSalesforce.rejected, (state, action) => {
        console.log('[Salesforce:Redux] ❌ initializeSalesforce.rejected - Setting error state');
        state.loading = false;
        state.error = action.payload;
        state.salesforce.isInitialized = false;
        
        // ✅ KEEP MODAL OPEN - don't navigate away
        // state.openModal stays as-is (don't close)
        
        // Stop any running timer
        if (state.salesforce.agentWaitTimer.timeoutId) {
          clearInterval(state.salesforce.agentWaitTimer.timeoutId);
        }
        
        // Reset timer state
        state.salesforce.agentWaitTimer = {
          isWaiting: false,
          timeoutId: null,
          startTime: null,
          remainingTime: 30,
          hasTimedOut: false,
          secondTimeout: false
        };
        
        // Clear conversation ID and messages
        state.salesforce.conversationId = null;
        state.items = null;
        
        // Clear salesforceService state and localStorage
        salesforceService.clearConversationId();
        salesforceService.setConversationStatus(CONVERSATION_STATUS.ERROR);
        
        // Clear localStorage conversation data
        try {
          clearLastEventId();
          clearConversationStartTime();
        } catch (error) {
          console.warn('[Salesforce:Redux] Failed to clear localStorage during error:', error);
        }
        
        // Set ERROR status
        state.salesforce.conversationStatus = CONVERSATION_STATUS.ERROR;
        console.log('[Salesforce:Redux] ❌ Error state set - modal remains open');
      })

      // Start Salesforce Conversation
      .addCase(startSalesforceConversation.pending, (state) => {
        console.log('[Salesforce:Redux] 📤 startSalesforceConversation.pending - Status before:', state.salesforce.conversationStatus, '| items:', state.items);
        state.loading = true;
        state.error = null;
        // ✅ FIX: Don't override AGENT_UNAVAILABLE or WAITING_FOR_AGENT status during pending
        if (state.salesforce.conversationStatus !== CONVERSATION_STATUS.AGENT_UNAVAILABLE &&
            state.salesforce.conversationStatus !== CONVERSATION_STATUS.WAITING_FOR_AGENT) {
          console.log('[Salesforce:Redux] 📤 startSalesforceConversation.pending - Setting status from', state.salesforce.conversationStatus, 'to CONNECTING');
          state.salesforce.conversationStatus = 'CONNECTING';
          console.log('[Salesforce:Redux] 📤 startSalesforceConversation.pending - Status set to CONNECTING');
        } else {
          console.log('[Salesforce:Redux] 📤 startSalesforceConversation.pending - Keeping status:', state.salesforce.conversationStatus);
        }
      })
      .addCase(startSalesforceConversation.fulfilled, (state, action) => {
        console.log('[Salesforce:Redux] ✅ startSalesforceConversation.fulfilled - Status before:', state.salesforce.conversationStatus);
        state.loading = false;
        
        // ✅ Mark that conversations API has been called (listConversations completed)
        state.conversationsLoaded = true;
        
        // ✅ Store conversations count from API response
        state.conversationsCount = action.payload.conversationsCount || 0;
        
        // Check if API returned existingMessages array with actual messages
        const existingMessages = action.payload.existingMessages;
        const hasExistingMessages = existingMessages && existingMessages.length > 0;
        console.log('[Salesforce:Redux] ✅ hasExistingMessages:', hasExistingMessages, 'count:', existingMessages?.length || 0, 'waitingForAgent:', action.payload.waitingForAgent);
        
        if (hasExistingMessages) {
          // API returned existingMessages with content - set items from history
          console.log('[Salesforce:Redux] ✅ Loaded', existingMessages.length, 'existing messages');
          state.items = existingMessages;
          
          // Check conversation status from payload
          const conversationStatus = action.payload.conversationStatus;
          console.log('[Salesforce:Redux] ✅ Conversation status from API:', conversationStatus, '| toUpperCase:', conversationStatus?.toUpperCase?.());
          
          // Determine Redux status based on conversation status
          // Priority order:
          // 1. If waitingForAgent flag is set (INITIAL, NEEDS_ROUTING, etc) -> WAITING_FOR_AGENT
          // 2. If status is ROUTED -> ACTIVE
          // 3. Otherwise -> ACTIVE
          console.log('[Salesforce:Redux] ✅ Checking status - conversationStatus:', conversationStatus, 'waitingForAgent:', action.payload.waitingForAgent);
          
          if (action.payload.waitingForAgent) {
            console.log('[Salesforce:Redux] ✅ waitingForAgent=true - Setting to WAITING_FOR_AGENT (input disabled, history visible)');
            state.salesforce.conversationStatus = CONVERSATION_STATUS.WAITING_FOR_AGENT;
            console.log('[Salesforce:Redux] ✅ FINAL STATUS SET TO:', state.salesforce.conversationStatus);
            
            // ✅ FIX: Start timer when restoring conversation in WAITING_FOR_AGENT state (page reload case)
            // This handles the scenario where page is reloaded while waiting for agent
            const { agentWaitTimer } = state.salesforce;
            if (!agentWaitTimer.isWaiting && !agentWaitTimer.hasTimedOut) {
              console.log('[Salesforce:Redux] 🔥 Page reload detected with WAITING_FOR_AGENT - will start timer');
              // Timer will be started in a separate dispatch after this reducer completes
            }
          } else if (conversationStatus && (conversationStatus.toUpperCase() === 'ROUTED' || conversationStatus.toUpperCase() === 'ACTIVE')) {
            console.log('[Salesforce:Redux] ✅ Status is', conversationStatus, '- Setting to ACTIVE (input enabled)');
            state.salesforce.conversationStatus = CONVERSATION_STATUS.ACTIVE;
            // ✅ ROUTED or ACTIVE means agent has already joined the conversation
            state.salesforce.hasAgentJoined = true;
            // ✅ On reload: set agentJoinedAt to NOW so it appears at the bottom
            // When new messages arrive, this timestamp stays fixed so position is correct
            state.salesforce.agentJoinedAt = Date.now();
            console.log('[Salesforce:Redux] ✅ FINAL STATUS SET TO:', state.salesforce.conversationStatus, '| hasAgentJoined:', state.salesforce.hasAgentJoined, '| agentJoinedAt:', state.salesforce.agentJoinedAt);
          } else {
            // Regular active conversation with history (unknown status)
            console.log('[Salesforce:Redux] ✅ Default (unknown status:', conversationStatus, ') - Setting status to ACTIVE');
            state.salesforce.conversationStatus = CONVERSATION_STATUS.ACTIVE;
            console.log('[Salesforce:Redux] ✅ FINAL STATUS SET TO:', state.salesforce.conversationStatus);
          }
        } else {
          // No existingMessages or empty array - this is a fresh conversation
          // Set status to CONNECTING, will transition to WAITING_FOR_AGENT when routing starts
          console.log('[Salesforce:Redux] ✅ New/fresh conversation (no history) - Setting status to CONNECTING');
          state.items = []; // Set to empty array instead of null
          state.salesforce.conversationStatus = CONVERSATION_STATUS.CONNECTING;
        }
        state.salesforce.conversationId = action.payload.conversationId;
        state.salesforce.isServiceReady = true;
      })
      .addCase(startSalesforceConversation.rejected, (state, action) => {
        console.log('[Salesforce:Redux] ❌ startSalesforceConversation.rejected - Status before:', state.salesforce.conversationStatus);
        state.loading = false;
        state.error = action.payload;
        
        // ✅ KEEP MODAL OPEN - don't navigate away
        // state.openModal stays as-is (don't close)
        
        // Stop any running timer
        if (state.salesforce.agentWaitTimer.timeoutId) {
          clearInterval(state.salesforce.agentWaitTimer.timeoutId);
        }
        
        // Reset timer state
        state.salesforce.agentWaitTimer = {
          isWaiting: false,
          timeoutId: null,
          startTime: null,
          remainingTime: 30,
          hasTimedOut: false,
          secondTimeout: false
        };
        
        // Clear conversation ID and messages
        state.salesforce.conversationId = null;
        state.items = null;
        
        // Clear salesforceService state and localStorage
        salesforceService.clearConversationId();
        salesforceService.setConversationStatus(CONVERSATION_STATUS.ERROR);
        
        // Clear localStorage conversation data
        try {
          clearLastEventId();
          clearConversationStartTime();
        } catch (error) {
          console.warn('[Salesforce:Redux] Failed to clear localStorage during error:', error);
        }
        
        // Set ERROR status
        state.salesforce.conversationStatus = CONVERSATION_STATUS.ERROR;
        console.log('[Salesforce:Redux] ❌ Error state set - modal remains open');
      })

      // Resume Salesforce Conversation
      .addCase(resumeSalesforceConversation.pending, (state) => {
        console.log('[Salesforce:Redux] 📤 resumeSalesforceConversation.pending - Status before:', state.salesforce.conversationStatus);
        state.loading = true;
        state.error = null;
        // ✅ FIX: Don't override AGENT_UNAVAILABLE or WAITING_FOR_AGENT status during pending
        if (state.salesforce.conversationStatus !== CONVERSATION_STATUS.AGENT_UNAVAILABLE &&
            state.salesforce.conversationStatus !== CONVERSATION_STATUS.WAITING_FOR_AGENT) {
          state.salesforce.conversationStatus = 'CONNECTING';
          console.log('[Salesforce:Redux] 📤 resumeSalesforceConversation.pending - Status set to CONNECTING');
        } else {
          console.log('[Salesforce:Redux] 📤 resumeSalesforceConversation.pending - Keeping status:', state.salesforce.conversationStatus);
        }
      })
      .addCase(resumeSalesforceConversation.fulfilled, (state, action) => {
        console.log('[Salesforce:Redux] ✅ resumeSalesforceConversation.fulfilled - Status before:', state.salesforce.conversationStatus);
        state.loading = false;
        
        // ✅ Mark that conversations API has been called (listConversations completed)
        state.conversationsLoaded = true;
        
        // ✅ CRITICAL FIX: Check if API returned messages (null, [], or array with items)
        // null = not fetched yet (show waiting)
        // [] = fetched but empty (show ACTIVE, no waiting)
        // [...] = has messages (show ACTIVE with messages)
        const messagesWereFetched = action.payload.messages !== undefined && action.payload.messages !== null;

        if (messagesWereFetched) {
          // API returned messages (could be [] or have messages)
          // Set items based on what was fetched
          state.items = action.payload.messages;
          console.log('[Salesforce:Redux] ✅ Resumed conversation - messages fetched (length:', action.payload.messages?.length || 0, ') - setting items and ACTIVE');
          
          // Store conversation start time from first message if available
          if (action.payload.messages.length > 0) {
            const sortedMessages = [...action.payload.messages].sort((a, b) => {
              const timeA = a.timestamp || a.createdOn || 0;
              const timeB = b.timestamp || b.createdOn || 0;
              return timeA - timeB;
            });
            const firstMessage = sortedMessages[0];
            if (firstMessage && (firstMessage.timestamp || firstMessage.createdOn)) {
              import('../services/webStorageService').then(({ storeConversationStartTime }) => {
                storeConversationStartTime(firstMessage.timestamp || firstMessage.createdOn);
              });
            }
          }
          
          // ✅ If conversation is being resumed successfully, agent has already joined
          // (resumeSalesforceConversation only succeeds if checkConversationStatus returns true = ACTIVE)
          // Regardless of whether there are messages or not, the conversation is active
          console.log('[Salesforce:Redux] ✅ Resumed active conversation - setting hasAgentJoined = true (messagesCount:', action.payload.messages.length, ')');
          state.salesforce.hasAgentJoined = true;
          // ✅ On reload/resume: set agentJoinedAt to NOW so it appears at the bottom
          // When new messages arrive, this timestamp stays fixed so position is correct
          state.salesforce.agentJoinedAt = Date.now();
          console.log('[Salesforce:Redux] ✅ agentJoinedAt set to:', state.salesforce.agentJoinedAt);
          
          state.salesforce.conversationStatus = CONVERSATION_STATUS.ACTIVE;
          
          // ✅ CRITICAL: Clear any running timer interval
          if (state.salesforce.agentWaitTimer.timeoutId) {
            console.log('[Salesforce:Redux] ✅ Clearing running timer interval:', state.salesforce.agentWaitTimer.timeoutId);
            clearInterval(state.salesforce.agentWaitTimer.timeoutId);
          }
          
          // Clear agent wait timer since this is a resumed conversation
          state.salesforce.agentWaitTimer = {
            isWaiting: false,
            timeoutId: null,
            startTime: null,
            remainingTime: 30,
            hasTimedOut: false,
            secondTimeout: false
          };
        } else if (state.salesforce.conversationStatus !== CONVERSATION_STATUS.AGENT_UNAVAILABLE &&
                   state.salesforce.conversationStatus !== CONVERSATION_STATUS.WAITING_FOR_AGENT) {
          // No messages array in response → Something went wrong, trigger timer flow
          state.salesforce.conversationStatus = CONVERSATION_STATUS.CONNECTING;
          console.log('[Salesforce:Redux] ✅ No messages in response - fresh conversation, setting to CONNECTING');
        } else {
          console.log('[Salesforce:Redux] ✅ resumeSalesforceConversation.fulfilled - Keeping status:', state.salesforce.conversationStatus);
        }
        
        state.salesforce.conversationId = action.payload.conversationId;
        state.salesforce.isServiceReady = true;
      })
      .addCase(resumeSalesforceConversation.rejected, (state, action) => {
        console.log('[Salesforce:Redux] ❌ resumeSalesforceConversation.rejected - Status before:', state.salesforce.conversationStatus);
        state.loading = false;
        
        // If conversation expired, just reset to NOT_STARTED (normal flow)
        if (action.payload === 'CONVERSATION_EXPIRED') {
          state.salesforce.conversationStatus = CONVERSATION_STATUS.NOT_STARTED;
          state.error = null;
          console.log('[Salesforce:Redux] ❌ resumeSalesforceConversation.rejected - Conversation expired, reset to NOT_STARTED');
        } else {
          // For other errors (API failures), set ERROR status and keep modal open
          console.log('[Salesforce:Redux] ❌ resumeSalesforceConversation.rejected - API failed, setting error state');
          state.error = action.payload;
          
          // ✅ KEEP MODAL OPEN - don't navigate away
          // state.openModal stays as-is (don't close)
          
          // Stop any running timer
          if (state.salesforce.agentWaitTimer.timeoutId) {
            clearInterval(state.salesforce.agentWaitTimer.timeoutId);
          }
          
          // Reset timer state
          state.salesforce.agentWaitTimer = {
            isWaiting: false,
            timeoutId: null,
            startTime: null,
            remainingTime: 30,
            hasTimedOut: false,
            secondTimeout: false
          };
          
          // Clear conversation ID and messages
          state.salesforce.conversationId = null;
          state.items = null;
          
          // Clear salesforceService state and localStorage
          salesforceService.clearConversationId();
          salesforceService.setConversationStatus(CONVERSATION_STATUS.ERROR);
          
          // Clear localStorage conversation data
          try {
            clearLastEventId();
            clearConversationStartTime();
          } catch (error) {
            console.warn('[Salesforce:Redux] Failed to clear localStorage during error:', error);
          }
          
          // Set ERROR status
          state.salesforce.conversationStatus = CONVERSATION_STATUS.ERROR;
          console.log('[Salesforce:Redux] ❌ Error state set - modal remains open');
        }
      })

      // Send Salesforce Message
      .addCase(sendSalesforceMessage.pending, (state, action) => {
        // Add optimistic message to UI
        const optimisticMessage = {
          id: `temp_${Date.now()}`,
          text: action.meta.arg,
          timestamp: Date.now(),
          sender: 'user',
          type: 'salesforce',
          status: 'sending',
          conversationId: state.salesforce.conversationId
        };
        state.items = [...(state.items || []), optimisticMessage];
        
        // Store conversation start time if this is the first message
        if (state.items && state.items.length === 1) {
          import('../services/webStorageService').then(({ storeConversationStartTime }) => {
            storeConversationStartTime(optimisticMessage.timestamp);
          });
        }
      })
      .addCase(sendSalesforceMessage.fulfilled, (state, action) => {
        // Update the optimistic message with server response
        const tempMessage = state.items.find(item =>
          item.status === 'sending' && item.type === 'salesforce'
        );
        if (tempMessage) {
          tempMessage.status = 'sent';
          tempMessage.id = action.payload.messageId || tempMessage.id;
          
          // Track this message ID to prevent duplicate from EventSource
          if (tempMessage.id) {
            state.salesforce.sentMessageIds.push(tempMessage.id);
            console.log('[Salesforce:Redux] Tracked sent message ID:', tempMessage.id);
          }
        }
      })
      .addCase(sendSalesforceMessage.rejected, (state, action) => {
        // Update failed message status
        const tempMessage = state.items.find(item =>
          item.status === 'sending' && item.type === 'salesforce'
        );
        if (tempMessage) {
          tempMessage.status = 'failed';
        }
        state.error = action.payload;
      })

      // End Salesforce Conversation
      .addCase(endSalesforceConversation.pending, (state) => {
        state.salesforce.conversationStatus = 'ENDING';
      })
      .addCase(endSalesforceConversation.fulfilled, (state) => {
        state.salesforce.conversationStatus = 'ENDED';
        state.salesforce.isServiceReady = false;
        state.salesforce.connectionStatus = CONNECTION_STATUS.DISCONNECTED;
        state.salesforce.isEventSourceConnected = false;
        
        // ✅ FIX: Close EventSource connection when conversation ends
        try {
          closeEventSource();
          console.log('[Salesforce:Redux] EventSource closed after conversation ended');
        } catch (error) {
          console.error('[Salesforce:Redux] Failed to close EventSource on conversation end:', error);
        }
      })
      .addCase(endSalesforceConversation.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Shutdown Salesforce
      .addCase(shutdownSalesforce.fulfilled, (state) => {
        state.salesforce = { ...initialState.salesforce };
        state.salesforce.isServiceReady = false;
      })
      
      // Handle agent wait timer async actions
      .addCase(startAgentWaitTimerAsync.fulfilled, (state, action) => {
        console.log('[Salesforce:Redux] ⏱️ startAgentWaitTimerAsync.fulfilled - Storing interval ID:', action.payload.intervalId);
        // Store the interval ID so it can be properly cleared later
        state.salesforce.agentWaitTimer.timeoutId = action.payload.intervalId;
        console.log('[Salesforce:Redux] ⏱️ Timer state updated with intervalId');
      });
  },
});

const extractEventDataTimestamp = (eventData) => {
  let eventTimeStamp = eventData.conversationEntry?.transcriptedTimestamp || eventData.conversationEntry?.clientTimestamp;
  if (!eventTimeStamp) {
    console.log("TIMESTAMP: Cannot get timestamp from eventData for the exact time, eventData: ", eventData);
    eventTimeStamp = Date.now();
  }
  return eventTimeStamp;
};

export const {
  // Existing actions
  chatMessageSend,
  chatMessageUpdateStatus,
  setChatUserWriting,
  closeChat,
  openChat,
  setChat,
  setItems,
  setLoading,
  setError,

  // Salesforce actions
  updateSalesforceStatus,
  updateSalesforceConnectionStatus,
  setSalesforceSessionInitState,
  setSalesforceConversationId,
  setSalesforceConversationStatus,
  handleSalesforceMessage,
  addRestoredMessages,
  setAgentName,
  setSalesforceUserContext,
  updateSalesforceParticipant,
  setSalesforceTypingStatus,
  setSalesforceRoutingAttributes,
  clearSalesforceData,
  resetConversation,
  restartConversation,
  // Agent wait timer actions
  startAgentWaitTimer,
  updateAgentWaitTimer,
  stopAgentWaitTimer,
  agentWaitTimeout,
  agentSecondTimeout,
  resetAgentWaitState,
  agentJoinedConversation,
  cancelConversationRequest,
  // Network monitoring actions
  setIsNetworkOnline,
} = supportMessageListRedux.actions;

// Selectors (inline, following project pattern)
export const selectSupportMessageState = (state) => state.supportMessageListRedux;
export const selectConversationsLoaded = (state) => state.supportMessageListRedux.conversationsLoaded;
export const selectConversationsCount = (state) => state.supportMessageListRedux.conversationsCount;
export const selectSalesforceState = (state) => state.supportMessageListRedux.salesforce;
export const selectSalesforceIsInitialized = (state) => state.supportMessageListRedux.salesforce.isInitialized;
export const selectSalesforceIsReady = (state) => state.supportMessageListRedux.salesforce.isServiceReady;
export const selectSalesforceSessionInitState = (state) => state.supportMessageListRedux.salesforce.sessionInitState;
export const selectSalesforceConversationStatus = (state) => state.supportMessageListRedux.salesforce.conversationStatus;
export const selectSalesforceConnectionStatus = (state) => state.supportMessageListRedux.salesforce.connectionStatus;
export const selectSalesforceCanSendMessage = (state) => {
  const salesforce = state.supportMessageListRedux.salesforce;
  return salesforce.isInitialized && salesforce.conversationStatus === CONVERSATION_STATUS.ACTIVE && salesforce.isServiceReady;
};

// Agent wait timer selectors
export const selectAgentWaitTimer = (state) => state.supportMessageListRedux.salesforce.agentWaitTimer;
export const selectIsWaitingForAgent = (state) => state.supportMessageListRedux.salesforce.agentWaitTimer.isWaiting;
export const selectAgentWaitRemainingTime = (state) => state.supportMessageListRedux.salesforce.agentWaitTimer.remainingTime;
export const selectHasAgentTimedOut = (state) => state.supportMessageListRedux.salesforce.agentWaitTimer.hasTimedOut;
export const selectIsNetworkOnline = (state) => state.supportMessageListRedux.salesforce.isNetworkOnline;
export const selectAgentName = (state) => state.supportMessageListRedux.salesforce.agentName;
export const selectSalesforceUserContext = (state) => state.supportMessageListRedux.salesforce.userContext;
export const selectHasAgentJoined = (state) => state.supportMessageListRedux.salesforce.hasAgentJoined;
export const selectAgentJoinedAt = (state) => state.supportMessageListRedux.salesforce.agentJoinedAt;

export default supportMessageListRedux.reducer;
